import React, { useEffect, useRef, useState } from "react";

import {
  Backdrop,
  CircularProgress,
  Grid,
  InputAdornment,
  makeStyles,
  Typography,
} from "@material-ui/core";

import {
  ArrayField,
  Datagrid,
  Edit,
  FormDataConsumer,
  FormTab,
  FunctionField,
  Loading,
  minValue,
  number,
  NumberField,
  NumberInput,
  required,
  SelectInput,
  TabbedForm,
  TextField,
  TextInput,
  Toolbar,
  RadioButtonGroupInput,
  useEditController,
  useNotify,
  useRefresh,
} from "react-admin";
import { useForm } from "react-final-form";

import {
  CustomFields,
  getCustomFieldsToSave,
} from "../../../components/common/CustomFields";
import { ForceTitle } from "../../../components/common/ForceTitle";
import { HistoryTab } from "../../../components/common/HistoryTab";
import { AreaZonePicker } from "../../../components/common/LocationPicker";
import { NotesTab } from "../../../components/common/NotesTab";
import { isAuthorized } from "../../../utils/appUtils";
import {
  shouldRefreshInventoryTrackCar,
  useTrackCarSubscriptionRefresh,
} from "../../../utils/useTrackCarSubscriptionRefresh";
import {
  AclType,
  InventoryField,
  InventoryStatus,
  LotTypes,
  UnitWeightOptions,
} from "../../../utils/constants";
import {
  getLocationFullName,
  isRailShipmentInTrackArea,
  isTruckToRail,
  sortByLocation,
} from "../../../utils/locationUtils";
import {
  getUnitName,
  isBulkCommodity,
  isInventoryFieldVisible,
  isPieceDimensionsVisible,
  unitOptionRenderer,
} from "../../../utils/loadTypeUtils";

import {
  exceptionRenderer,
  getBackPath,
  trimDecimal,
} from "../../../utils/utils";
import { DialogSaveButton } from "./DialogSaveButton";
import { AuditStatusIcon } from "./AuditStatusIcon";
import { DeepDeleteButton } from "./DeepDeleteButton";
import { ValidateRequiredIcon } from "./ValidateRequiredIcon";
import { InventoryAside } from "./InventoryAside";
import { LinkProductDialog } from "./LinkProductDialog";
import { CreateInventoryItemButton } from "./inventoryItems/CreateInventoryItemButton";
import { OpenInventoryItemButton } from "./inventoryItems/OpenInventoryItemButton";
import { DeepRemoveButton } from "./inventoryItems/DeepDeleteButton";
import InventoryEditActions from "./InventoryEditActions";
import { fetchInventoryEditInbound } from "./InventoryEditUtils";
import { getInfoBubble, showWeight } from "./InventoryUitls";
import InventoryEditAdvice from "./advice/InventoryEditAdvice";

const useStyles = makeStyles((theme) => ({
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
  },
}));

const validateRequired = required();
const validateQtyRequired = [required(), number(), minValue(0)];
const validatePieces = [number(), minValue(1)];
const validateDimensions = [number(), minValue(0)];

const validateQty = (values) => {
  const errors = {};

  if (values.quantityReceived != null && values.quantityReceived !== "") {
    // NumberInput can leave these as strings in form state; coerce before
    // arithmetic/comparison so `+` doesn't concatenate and `!==` doesn't
    // fail on a string-vs-number type mismatch (see utils.js normalizeException).
    const quantityAdvised = Number(values.quantityAdvised || 0);
    const quantityReceived = Number(values.quantityReceived || 0);
    // Only fold the over/under quantities into the net when their flags are
    // actually set (mirrors validateTallyQty). Item-level exception rollups
    // (e.g. DeepDeleteButton) can clear the over/under flags while leaving a
    // stale over/underShipmentQuantity on the parent; applying it here
    // unconditionally produced a false mismatch (e.g. 20 received + stale 4
    // undershipment != 20 advised) when only "Damaged" was set.
    let netQuantityReceived = quantityReceived;
    if (values.overShipment || values.underShipment) {
      const underShipmentQuantity = Number(values.underShipmentQuantity || 0);
      const overShipmentQuantity = Number(values.overShipmentQuantity || 0);
      netQuantityReceived = trimDecimal(
        quantityReceived + underShipmentQuantity - overShipmentQuantity,
        3,
      );
    }

    if (quantityAdvised !== netQuantityReceived) {
      errors.quantityAdvised = "Qty Received mismatch";
      errors.quantityReceived = "Qty Advised mismatch";
    } else {
      errors.quantityAdvised = false;
      errors.quantityReceived = false;
    }
  }
  if (
    values.quantityMismatch &&
    values.status === InventoryStatus.STORED &&
    Number(values.quantityOnHand || 0) !== Number(values.quantityPhysical || 0)
  ) {
    errors.quantityOnHand = "Should equal Qty Physical";
    errors.quantityPhysical = "Should equal Qty On Hand";
  } else {
    errors.quantityOnHand = false;
    errors.quantityPhysical = false;
  }

  return errors;
};

const validateNotes = (form, formData) => {
  const notesMaxIndex = formData.notes ? formData.notes.length - 1 : -1;

  debugger;
  const qtyAdvisedState = form.getFieldState("quantityAdvised");
  const qtyReceivedState = form.getFieldState("quantityReceived");
  const qtyOnHandState = form.getFieldState("quantityOnHand");
  const qtyPhysicalState = form.getFieldState("quantityPhysical");

  const qtyAdvisedChanged = qtyAdvisedState?.dirty;
  const qtyReceivedChanged = qtyReceivedState?.dirty;
  const qtyOnHandChanged = qtyOnHandState?.dirty;
  const qtyPhysicalChanged = qtyPhysicalState?.dirty;

  if (
    (qtyAdvisedChanged ||
      qtyReceivedChanged ||
      qtyOnHandChanged ||
      qtyPhysicalChanged) &&
    (notesMaxIndex < 0 ||
      (notesMaxIndex >= 0 &&
        (!formData.notes[notesMaxIndex] || formData.notes[notesMaxIndex].date)))
  ) {
    //Trigger Add a Note to document changes
    return true;
  }

  return false;
};

const InventorySetupTab = ({
  backPath,
  record,
  inbound,
  productIsOpen,
  containerDrop,
  setProductIsOpen,
  exceptions,
  setExceptions,
  setQuantityAdvised,
  ...props
}) => {
  const form = useForm();
  const yardCommodities = window.appUser.reloadYard.activeYardCommodities.items;
  const [inventoryItems, setInventoryItems] = useState({ items: [] });

  //*************************     create another form in case of inbound advice  */
  // get it from url ?advice=true
  const isAdvice =
    record.status === InventoryStatus.ADVICE_REVIEW ||
    new URLSearchParams(window.location.search).get("advice");
  useEffect(() => {
    if (record?.inventoryItems) {
      let inventoryItemList = record.inventoryItems?.items || [];
      inventoryItemList = inventoryItemList.filter(
        (itm) =>
          itm.status !== InventoryStatus.SHIPPED ||
          itm.status !== InventoryStatus.ARCHIVED,
      );
      sortByLocation(inventoryItemList);

      inventoryItemList.forEach((inventoryItem) => {
        const shallowInventory = { ...record, inventoryItems: undefined };
        inventoryItem.inventory = shallowInventory;
      });

      setInventoryItems({ items: inventoryItemList });
    }
  }, [record]);

  // Subscribe to field state updates for 3rd party inputs
  form.registerField(
    "productId",
    (fieldState) => {
      const { change, ...rest } = fieldState;
    },
    { dirty: true, touched: true, valid: true, value: true },
  );
  form.registerField(
    "productCode",
    (fieldState) => {
      const { change, ...rest } = fieldState;
    },
    { dirty: true, touched: true, valid: true, value: true },
  );

  form.registerField(
    "hasException",
    (fieldState) => {
      const { blur, change, focus, ...rest } = fieldState;
    },
    { active: true, dirty: true, touched: true, valid: true, value: true },
  );
  form.registerField(
    "overShipment",
    (fieldState) => {
      const { blur, change, focus, ...rest } = fieldState;
    },
    { active: true, dirty: true, touched: true, valid: true, value: true },
  );
  form.registerField(
    "overShipmentQuantity",
    (fieldState) => {
      const { blur, change, focus, ...rest } = fieldState;
    },
    { active: true, dirty: true, touched: true, valid: true, value: true },
  );
  form.registerField(
    "overShipmentNote",
    (fieldState) => {
      const { blur, change, focus, ...rest } = fieldState;
    },
    { active: true, dirty: true, touched: true, valid: true, value: true },
  );
  form.registerField(
    "underShipment",
    (fieldState) => {
      const { blur, change, focus, ...rest } = fieldState;
    },
    { active: true, dirty: true, touched: true, valid: true, value: true },
  );
  form.registerField(
    "underShipmentQuantity",
    (fieldState) => {
      const { blur, change, focus, ...rest } = fieldState;
    },
    { active: true, dirty: true, touched: true, valid: true, value: true },
  );
  form.registerField(
    "underShipmentNote",
    (fieldState) => {
      const { blur, change, focus, ...rest } = fieldState;
    },
    { active: true, dirty: true, touched: true, valid: true, value: true },
  );
  form.registerField(
    "isDamaged",
    (fieldState) => {
      const { blur, change, focus, ...rest } = fieldState;
    },
    { active: true, dirty: true, touched: true, valid: true, value: true },
  );
  form.registerField(
    "isRecalled",
    (fieldState) => {
      const { blur, change, focus, ...rest } = fieldState;
    },
    { active: true, dirty: true, touched: true, valid: true, value: true },
  );
  form.registerField(
    "recallNote",
    (fieldState) => {
      const { blur, change, focus, ...rest } = fieldState;
    },
    { active: true, dirty: true, touched: true, valid: true, value: true },
  );
  form.registerField(
    "otherException",
    (fieldState) => {
      const { blur, change, focus, ...rest } = fieldState;
    },
    { active: true, dirty: true, touched: true, valid: true, value: true },
  );
  form.registerField(
    "otherExceptionNote",
    (fieldState) => {
      const { blur, change, focus, ...rest } = fieldState;
    },
    { active: true, dirty: true, touched: true, valid: true, value: true },
  );

  useEffect(() => {
    debugger;
    form.change("hasException", exceptions?.hasException);
    form.change("overShipment", exceptions?.overShipment);
    form.change("overShipmentQuantity", exceptions?.overShipmentQuantity);
    form.change("overShipmentNote", exceptions?.overShipmentNote);
    form.change("underShipment", exceptions?.underShipment);
    form.change("underShipmentQuantity", exceptions?.underShipmentQuantity);
    form.change("underShipmentNote", exceptions?.underShipmentNote);
    form.change("isDamaged", exceptions?.isDamaged);
    form.change("isRecalled", exceptions?.isRecalled);
    form.change("recallNote", exceptions?.recallNote);
    form.change("otherException", exceptions?.otherException);
    form.change("otherExceptionNote", exceptions?.otherExceptionNote);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exceptions]);

  const handleProductLink = (product, rename) => {
    debugger;
    form.change("productId", product?.id ? product.id : null);
    form.change(
      "productCode",
      product?.productCode ? product.productCode : null,
    );
    // The product owns the load type and its units, the same way the inbound
    // catalog add and manifest import stamp them from the product — but only
    // until the row is received. A received row's quantities were recorded in
    // its own UOM, and storage rates are looked up per UOM, so restamping the
    // UOM (or the load type that picks the storage rule) would rebill the same
    // numbers at another unit's rate. Unlink passes no product and leaves both
    // alone.
    if (!record.receivedAt) {
      if (product?.reloadYardCommodityId) {
        form.change("reloadYardCommodityId", product.reloadYardCommodityId);
      }
      if (product?.defaultUOM) {
        form.change("inventoryUOM", product.defaultUOM);
      }
    }
    if (rename && product?.name) {
      form.change("productName", product.name);
    }
    if (product?.customFields) {
      const customFieldsToUpdate = getCustomFieldsToSave(
        product.customFields,
        record.account.inventoryCustomFields,
      );
      form.change(
        "customFields",
        customFieldsToUpdate ? customFieldsToUpdate : null,
      );
    }
    if (product?.defaultWeightUOM) {
      form.change("weightUOM", product.defaultWeightUOM);
      if (!record.weight)
        form.change("weight", product[product.defaultWeightUOM.toLowerCase()]);
    } else {
      form.change("weightUOM", record.account?.defaultWeightUOM);
    }
  };

  const handleLocation = (area, zone) => {
    debugger;
    form.change("area", area);

    if (zone) {
      form.change("zoneId", zone.id);
      form.change("zone", zone);
    } else {
      form.change("zoneId", "");
      form.change("zone", null);
    }
  };

  const canAssignLocations = (formData) => {
    const area = formData.area || formData.areaId;
    return (
      formData.status !== InventoryStatus.STORED &&
      formData.status !== InventoryStatus.SHIPPED &&
      isAuthorized(AclType.ASSIGN_LOCATION) &&
      (!isRailShipmentInTrackArea(area, formData.inboundShipment) ||
        isTruckToRail(area, formData.inboundShipment))
    );
  };

  const isOnHand = (formData) => {
    return (
      formData.status !== InventoryStatus.IN_TRANSIT &&
      formData.status !== InventoryStatus.ARRIVED
    );
  };

  const getQtyLabel = (formData) => {
    return isOnHand(formData) ? "Qty. On Hand" : "Putaway Qty.";
  };

  const renderUnits = () => getUnitName(record.inventoryUOM);

  const ListActionButtons = ({ record: inventoryItem, ...rest }) => {
    const [isDeleting, setIsDeleting] = useState(false);

    return (
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <OpenInventoryItemButton
          {...props}
          form={form}
          record={inventoryItem}
          backPath={backPath}
          inventory={record}
          isDeleting={isDeleting}
        />
        &nbsp;
        {(!isOnHand(record) || record.verificationRequired === "true") && (
          <DeepRemoveButton
            record={inventoryItem}
            inventory={record}
            exceptions={exceptions}
            setExceptions={setExceptions}
            inventoryItems={inventoryItems}
            setInventoryItems={setInventoryItems}
            isDeleting={isDeleting}
            setIsDeleting={setIsDeleting}
          />
        )}
      </div>
    );
  };

  const handleQuantityAdvisedChange = (event) => {
    setQuantityAdvised(+event.target.value || 0);
  };

  const getWeightHelperText = (formData) => {
    if (formData.weight && formData.quantityOnHand) {
      const formatter = new Intl.NumberFormat("en-US");
      return `Net: ${formatter.format(
        trimDecimal(formData.weight * formData.quantityOnHand, 3),
      )} ${formData.weightUOM}`;
    }
    return false;
  };

  return (
    <FormDataConsumer>
      {({ formData, ...rest }) =>
        isAdvice ? (
          <InventoryEditAdvice
            {...props}
            backPath={backPath}
            productIsOpen={productIsOpen}
            setProductIsOpen={setProductIsOpen}
            handleQuantityAdvisedChange={handleQuantityAdvisedChange}
            formData={formData}
            record={record}
            validateQty={validateQty}
            props={props}
            handleProductLink={handleProductLink}
            inventoryItems={inventoryItems}
            ListActionButtons={ListActionButtons}
          />
        ) : (
          <Grid container direction="column" spacing={0}>
            <Grid container direction="row" spacing={1}>
              <Grid item xs={12} md={6}>
                <TextInput
                  source="productName"
                  label="Product Name"
                  variant="outlined"
                  fullWidth
                  disabled={record.productId}
                  validate={validateRequired}
                  helperText={false}
                  InputProps={{
                    endAdornment: getInfoBubble(formData),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextInput
                  source="lotNumber"
                  label="Lot Number"
                  variant="outlined"
                  fullWidth
                  disabled={true}
                  helperText={false}
                />
              </Grid>
            </Grid>
            {record.status !== InventoryStatus.STORED &&
              record.status !== InventoryStatus.SHIPPED && (
                <Grid container item direction="row" spacing={1}>
                  <Grid item xs={12} md={6}>
                    <NumberInput
                      source="quantityAdvised"
                      label="Quantity Advised"
                      variant="outlined"
                      fullWidth
                      disabled={
                        formData.status === InventoryStatus.STORED ||
                        formData.status === InventoryStatus.SHIPPED ||
                        formData.status === InventoryStatus.RELEASED
                      }
                      min="0"
                      onChange={handleQuantityAdvisedChange}
                      validate={validateQtyRequired}
                      error={validateQty(formData).quantityAdvised}
                      helperText={
                        record?.quantityPrecount != null
                          ? `Pre-count: ${record.quantityPrecount}`
                          : validateQty(formData).quantityAdvised || false
                      }
                      FormHelperTextProps={{
                        style: {
                          color:
                            record?.quantityPrecount != null &&
                            formData?.quantityAdvised !==
                              record.quantityPrecount
                              ? "#f44336"
                              : undefined,
                        },
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            {" "}
                            {getUnitName(record.inventoryUOM)}{" "}
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <NumberInput
                      source="quantityReceived"
                      label="Quantity Received"
                      variant="outlined"
                      fullWidth
                      disabled={true}
                      min="0"
                      // skip validation to allow saving with errors in some states
                      error={validateQty(formData).quantityReceived}
                      helperText={
                        validateQty(formData).quantityReceived || false
                      }
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            {" "}
                            <ValidateRequiredIcon
                              record={formData}
                              {...props}
                            />{" "}
                            {getUnitName(record.inventoryUOM)}{" "}
                          </InputAdornment>
                        ),
                        style: { textTransform: "uppercase" },
                      }}
                    />
                  </Grid>
                  {isInventoryFieldVisible(
                    InventoryField.PCS,
                    record.reloadYardCommodity?.commodity,
                  ) && (
                    <Grid item xs={12} md={6}>
                      <NumberInput
                        source="pcs"
                        label="Pieces / Unit"
                        variant="outlined"
                        fullWidth
                        min="1"
                        placeholder={record.product?.pcs}
                        InputLabelProps={{
                          shrink:
                            formData?.pcs != null ||
                            record?.product?.pcs != null, //not null or undefined
                        }}
                        validate={validatePieces}
                        helperText={false}
                      />
                    </Grid>
                  )}
                </Grid>
              )}
            {(record.status === InventoryStatus.STORED ||
              record.status === InventoryStatus.SHIPPED) && (
              <Grid container item direction="row" spacing={1}>
                <Grid item xs={12} md={6}>
                  <NumberInput
                    source="quantityOnHand"
                    label="Quantity On Hand"
                    variant="outlined"
                    fullWidth
                    min="0"
                    validate={
                      record.status === InventoryStatus.STORED
                        ? validateQtyRequired
                        : undefined
                    }
                    error={validateQty(formData).quantityOnHand}
                    helperText={validateQty(formData).quantityOnHand}
                    disabled={
                      formData.status === InventoryStatus.STORED ||
                      formData.status === InventoryStatus.SHIPPED
                    }
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          {" "}
                          {getUnitName(record.inventoryUOM)}{" "}
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                {record.quantityMismatch && (
                  <Grid item xs={12} md={6}>
                    <NumberInput
                      source="quantityPhysical"
                      label="Quantity Physical"
                      variant="outlined"
                      fullWidth
                      min="0"
                      error={validateQty(formData).quantityPhysical}
                      helperText={validateQty(formData).quantityPhysical}
                      disabled={
                        formData.status !== InventoryStatus.STORED ||
                        formData.status === InventoryStatus.SHIPPED
                      }
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            {" "}
                            <AuditStatusIcon
                              record={formData}
                              {...props}
                            />{" "}
                            {getUnitName(record.inventoryUOM)}{" "}
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                )}
                {isInventoryFieldVisible(
                  InventoryField.PCS,
                  record.reloadYardCommodity?.commodity,
                ) && (
                  <Grid item xs={12} md={6}>
                    <NumberInput
                      source="pcs"
                      label="Pieces / Unit"
                      variant="outlined"
                      fullWidth
                      min="1"
                      placeholder={record.product?.pcs}
                      InputLabelProps={{
                        shrink:
                          formData?.pcs != null || record?.product?.pcs != null,
                      }}
                      validate={validatePieces}
                      helperText={false}
                    />
                  </Grid>
                )}
              </Grid>
            )}
            {(record.status === InventoryStatus.STORED ||
              record.status === InventoryStatus.SHIPPED) && (
              <Grid container direction="row" spacing={1}>
                <Grid item xs={12} md={6}>
                  <NumberInput
                    source="quantityCommitted"
                    label="Quantity Committed"
                    variant="outlined"
                    fullWidth
                    disabled={true}
                    helperText={false}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          {" "}
                          {getUnitName(record.inventoryUOM)}{" "}
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <NumberInput
                    source="quantityAvailable"
                    label="Quantity Available"
                    variant="outlined"
                    fullWidth
                    // defaultValue={formData.quantityOnHand - formData.quantityCommitted}
                    format={() =>
                      trimDecimal(
                        formData.quantityOnHand - formData.quantityCommitted,
                        3,
                      )
                    }
                    disabled={true}
                    helperText={false}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          {" "}
                          {getUnitName(record.inventoryUOM)}{" "}
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>
            )}
            {showWeight(record.account, record) && (
              <Grid container direction="row" spacing={1}>
                <Grid item xs={12} md={6}>
                  <NumberInput
                    source="weight"
                    label={`Weight: ${formData.weightUOM} / ${formData.inventoryUOM}`}
                    variant="outlined"
                    fullWidth
                    disabled={
                      formData.status === InventoryStatus.SHIPPED ? true : false
                    }
                    validate={
                      formData?.weightUOM ? validateRequired : undefined
                    }
                    helperText={getWeightHelperText(formData)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <SelectInput
                    source="weightUOM"
                    label="Weight UOM"
                    variant="outlined"
                    fullWidth
                    allowEmpty
                    choices={UnitWeightOptions}
                    optionText={unitOptionRenderer}
                    disabled={
                      formData.status === InventoryStatus.SHIPPED ? true : false
                    }
                    helperText={false}
                  />
                </Grid>
              </Grid>
            )}
            {productIsOpen && (
              <Grid container spacing={0}>
                <Grid item xs={false}>
                  <LinkProductDialog
                    {...props}
                    record={formData}
                    backPath={backPath}
                    isOpen={productIsOpen}
                    setIsOpen={setProductIsOpen}
                    handleAddSelected={handleProductLink}
                  />
                </Grid>
              </Grid>
            )}
            <Grid container direction="row" spacing={1}>
              <Grid item xs={12} md={6}>
                <RadioButtonGroupInput
                  source="lotType"
                  label="Lot Type"
                  row
                  fullWidth
                  disabled={true}
                  helperText={false}
                  choices={LotTypes}
                  style={{ marginTop: 0 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <SelectInput
                  source="reloadYardCommodityId"
                  label="Load Type"
                  variant="outlined"
                  fullWidth
                  disabled={
                    record.productId || inventoryItems?.items.length > 0
                  }
                  choices={yardCommodities}
                  optionText="commodity.name"
                  optionValue="id"
                  validate={validateRequired}
                  helperText={false}
                />
              </Grid>
            </Grid>
            {isPieceDimensionsVisible(
              record.reloadYardCommodity?.commodity,
            ) && (
              <Grid container direction="row" spacing={1}>
                <Grid item xs={12} style={{ paddingBottom: 0 }}>
                  <Typography variant="overline" color="textSecondary">
                    PIECE DIMENSIONS
                  </Typography>
                </Grid>
                {isInventoryFieldVisible(
                  InventoryField.HEIGHT,
                  record.reloadYardCommodity?.commodity,
                ) && (
                  <Grid item xs={12} md={4}>
                    <NumberInput
                      source="pcsHeight"
                      label="Piece Height"
                      variant="outlined"
                      fullWidth
                      placeholder={record?.product?.pcsHeight}
                      InputLabelProps={{
                        shrink:
                          record?.product?.pcsHeight != null ||
                          formData?.pcsHeight != null, //not null or undefined
                      }}
                      validate={validateDimensions}
                      helperText={false}
                      InputProps={{
                        inputMode: "decimal",
                        endAdornment: (
                          <InputAdornment position="end">IN</InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                )}
                {isInventoryFieldVisible(
                  InventoryField.WIDTH,
                  record.reloadYardCommodity?.commodity,
                ) && (
                  <Grid item xs={12} md={4}>
                    <NumberInput
                      source="pcsWidth"
                      label="Pice Width"
                      variant="outlined"
                      fullWidth
                      placeholder={record.product?.pcsWidth}
                      InputLabelProps={{
                        shrink:
                          record?.product?.pcsWidth != null ||
                          formData?.pcsWidth != null, //not null or undefined
                      }}
                      validate={validateDimensions}
                      helperText={false}
                      InputProps={{
                        inputMode: "decimal",
                        endAdornment: (
                          <InputAdornment position="end">IN</InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                )}
                {isInventoryFieldVisible(
                  InventoryField.LENGTH,
                  record.reloadYardCommodity?.commodity,
                ) && (
                  <Grid item xs={12} md={4}>
                    <NumberInput
                      source="pcsLength"
                      label="Length"
                      variant="outlined"
                      fullWidth
                      placeholder={
                        record.product?.pcsLength || formData?.pcsLength
                      }
                      InputLabelProps={{
                        shrink:
                          record?.product?.pcsLength != null ||
                          formData?.pcsLength != null, //not null or undefined
                      }}
                      validate={validateDimensions}
                      helperText={false}
                      InputProps={{
                        inputMode: "decimal",
                        endAdornment: (
                          <InputAdornment position="end">FT</InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                )}
              </Grid>
            )}
            <Grid container direction="row" spacing={1}>
              <CustomFields
                {...props}
                recordFields={record.customFields}
                accountFields={record.account.inventoryCustomFields}
                width={4}
                disabled={false}
              />
              <Grid item container direction="row" spacing={1}>
                <Grid item xs={12}>
                  <Typography variant="overline" color="textSecondary">
                    Locations:
                  </Typography>
                  <br />
                </Grid>

                {inventoryItems?.items?.length === 0 && (
                  <Grid item xs={12}>
                    <AreaZonePicker
                      areaLabel="Default Area"
                      zoneLabel="Default Zone"
                      inventory={formData}
                      handleLocation={handleLocation}
                      disabled={
                        inbound.sit ||
                        formData.status === InventoryStatus.STORED ||
                        formData.status === InventoryStatus.AGGREGATED ||
                        formData.status === InventoryStatus.SHIPPED ||
                        inventoryItems?.items?.length > 0 ||
                        isBulkCommodity(record.reloadYardCommodity?.commodity)
                      }
                    />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <ArrayField source="items" record={inventoryItems}>
                    <Datagrid>
                      <FunctionField
                        source="hasException"
                        label=" "
                        render={exceptionRenderer}
                      />
                      <FunctionField
                        label="Location"
                        render={(record) =>
                          getLocationFullName(
                            record.location || record.locationFullName,
                          )
                        }
                      />
                      {isRailShipmentInTrackArea(formData.areaId, inbound) && (
                        <TextField
                          source="inventory.railCarNumber"
                          label="Car Number"
                        />
                      )}

                      <NumberField
                        source="quantityOnHand"
                        label={getQtyLabel(formData)}
                      />
                      {isOnHand(formData) && (
                        <NumberField
                          source="quantityCommitted"
                          label="Qty. Committed"
                        />
                      )}
                      <FunctionField label="UOM" render={renderUnits} />
                      {formData.status !== InventoryStatus.IN_TRANSIT && (
                        <TextField source="unloadedBy" label="Unloaded By" />
                      )}
                      {isAuthorized(AclType.ASSIGN_LOCATION) && (
                        <ListActionButtons />
                      )}
                    </Datagrid>
                  </ArrayField>
                </Grid>
                <Grid item xs={12}>
                  {!containerDrop && canAssignLocations(formData) && (
                    <CreateInventoryItemButton
                      {...props}
                      backPath={backPath}
                      form={form}
                      inventory={record}
                    />
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        )
      }
    </FormDataConsumer>
  );
};

const EditToolbar = (props) => {
  const classes = useStyles();
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  return (
    <Toolbar style={{ justifyContent: "space-between" }}>
      <Backdrop className={classes.backdrop} open={isSaving || isDeleting}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <DialogSaveButton
        {...props}
        validateQty={validateQty}
        validateNotes={validateNotes}
        isSaving={isSaving}
        setIsSaving={setIsSaving}
        isDeleting={isDeleting}
      />
      <DeepDeleteButton
        {...props}
        isSaving={isSaving}
        isDeleting={isDeleting}
        setIsDeleting={setIsDeleting}
      />
    </Toolbar>
  );
};

export const InventoryEdit = (props) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const { record } = useEditController(props);

  const reloadYardId = window.appUser?.reloadYardId;

  useTrackCarSubscriptionRefresh({
    enabled: Boolean(reloadYardId && record?.id),
    label: "InventoryEdit",
    shouldRefresh: (trackCar) => {
      return shouldRefreshInventoryTrackCar({
        trackCar,
        reloadYardId,
        inboundShipmentId: record?.inboundShipmentId,
        recordId: record?.id,
      });
    },
    onRefresh: () => {
      setTimeout(() => refresh(), 2500);
      setTimeout(() => refresh(), 6000);
    },
  });

  const [reconcileAudit] = useState(props.location?.state?.audit);
  const [inbound, setInbound] = useState(
    props.location?.state?.inboundRecord || record?.inboundShipment,
  );
  const [productIsOpen, setProductIsOpen] = useState(false);
  const [exceptions, setExceptions] = useState({}); // exceptions set in ExceptionDialog & inventoryItemDeepRemove
  const [quantityAdvised, setQuantityAdvised] = useState(
    record?.quantityAdvised,
  );
  const backPath = getBackPath(props);
  const prevInventoryRecordIdRef = useRef(null);

  useEffect(() => {
    if (!record) return;

    setQuantityAdvised(record.quantityAdvised);
    setExceptions({
      hasException: record.hasException,
      overShipment: record.overShipment,
      overShipmentQuantity: record.overShipmentQuantity,
      overShipmentNote: record.overShipmentNote,
      underShipment: record.underShipment,
      underShipmentQuantity: record.underShipmentQuantity,
      underShipmentNote: record.underShipmentNote,
      isDamaged: record.isDamaged,
      isRecalled: record.isRecalled,
      recallNote: record.recallNote,
      otherException: record.otherException,
      otherExceptionNote: record.otherExceptionNote,
    });

    const inventoryRecordIdChanged =
      prevInventoryRecordIdRef.current !== record.id;

    if (!inventoryRecordIdChanged) return;

    prevInventoryRecordIdRef.current = record.id;

    const embeddedInbound = record.inboundShipment;
    const navInbound = props.location?.state?.inboundRecord;
    const navInboundMatchesRecord =
      Boolean(navInbound && record.inboundShipmentId) &&
      navInbound.id === record.inboundShipmentId;
    const inboundForRecord =
      embeddedInbound ?? (navInboundMatchesRecord ? navInbound : null);

    setInbound(inboundForRecord);

    if (!inboundForRecord && record.inboundShipmentId) {
      fetchInventoryEditInbound({ record, setInbound, notify });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record, props.location?.state?.inboundRecord]);

  const inboundShipmentIdMismatch =
    Boolean(record?.inboundShipmentId && inbound?.id) &&
    record.inboundShipmentId !== inbound.id;

  if (!record?.account || !inbound || inboundShipmentIdMismatch) {
    return <Loading />;
  }
  return (
    <Edit
      {...props}
      title={<ForceTitle title="Edit Inventory" />}
      mutationMode="pessimistic"
      aside={
        <InventoryAside
          {...props}
          backPath={backPath}
          exceptions={exceptions}
        />
      }
      actions={
        <InventoryEditActions
          {...props}
          backPath={backPath}
          data={record}
          setProductIsOpen={setProductIsOpen}
          exceptions={exceptions}
          setExceptions={setExceptions}
          quantityAdvised={quantityAdvised}
        />
      }
    >
      <TabbedForm
        variant="outlined"
        submitOnEnter={false}
        enableReinitialize={true} // STAFF LEVEL FIX: Ensure form resets when record is refreshed via subscriptions
        toolbar={
          <EditToolbar
            {...props}
            backPath={backPath}
            record={record}
            reconcileAudit={reconcileAudit}
          />
        }
      >
        <FormTab path="" label="Inventory Setup">
          <InventorySetupTab
            {...props}
            inbound={inbound}
            containerDrop={inbound.containerDrop}
            backPath={backPath}
            productIsOpen={productIsOpen}
            setProductIsOpen={setProductIsOpen}
            exceptions={exceptions}
            setExceptions={setExceptions}
            setQuantityAdvised={setQuantityAdvised}
          />
        </FormTab>
        <FormTab path="notes" label="Notes">
          <NotesTab />
        </FormTab>
        <FormTab path="historyLogs" label="History Log">
          <HistoryTab />
        </FormTab>
      </TabbedForm>
    </Edit>
  );
};
